import {
  CheckIcon,
  GlobalStyles,
  LuxButton,
  LuxLoader,
  LuxSplitedButton,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import { FormIds } from '@utils/enums/FormIds.enum';
import { useTranslation } from 'react-i18next';

import {
  ButtonsWrapper,
  ExternalWrapper,
  LeftButtonsWrapper,
} from '../Steps/Steps.styles';
import {
  MessageWrapper,
  SubtitleWrapper,
  ThankYouMessageWrapper,
  TypWrapper,
} from './ThankYouPage.styles';
import {
  AppointmentBody,
  // AppointmentResponse,
  GeneralConfigDTO,
} from '@utils/interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import usePutAppointment from '@hooks/usePutAppointment';
import { PatientStore, usePatientStore } from '@store/patientStore';
import {
  OnlineBookingStore,
  useOnlineBookingStore,
} from '@store/onlineBookingStore';
import {
  conditionallyApplyUpperCase,
  matchPatientWithSlotIds,
  resetApp,
} from '@utils/functions';
import AppointmentErrorPage from './AppointmentErrorPage';
import { ErrorCodes } from '@utils/enums/ErrorCodes.enum';
import BookingDetailsTyp from '@components/BookingDetails/BookingDetailsTyp';
import useGetTextColor from '@hooks/useGetTextColor';
import { createEvent, EventAttributes } from 'ics';
import { useHistory, useLocation } from 'react-router-dom';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import useAnalytics from '@hooks/useAnalytics';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';
import {
  IntegrationModes,
  isEmbeddedIntegrationMode,
} from '@utils/enums/IntegrationModes.enum';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
import { BrandNameList } from '@utils/consts/brandNameList';
import { buildAppointmentBodyForSave } from './ThankYouPage.utils';

export interface CurrentData {
  type: FormIds;
  data: any;
}

export interface ThankYouPageProps {
  config: GeneralConfigDTO;
}

const ThankYouPage = ({ config }: ThankYouPageProps) => {
  // #region Hooks

  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const { Urls } = useUrlsContext();
  const history = useHistory();
  const { trackPageAnalytics } = useAnalytics();
  const isMobile = useMediaQuery(`(max-width:${MobileBreakpoint}px)`);

  const brandName = useOnlineBookingStore(state => state.brandName);
  const isFromRescheduleFlow = useMemo(
    () => location.pathname.includes(Urls.RESCHEDULE_CONFIRMED),
    [location, Urls.RESCHEDULE_CONFIRMED],
  );

  const [errorType, setErrorType] = useState<ErrorCodes | undefined>();
  const patientStore = usePatientStore(state => state);
  const onlineBookingStore = useOnlineBookingStore(state => state);
  const [patientStoreCopy, setPatientStoreCopy] = useState<PatientStore>();
  const [onlineBookingStoreCopy, setOnlineBookingStoreCopy] =
    useState<OnlineBookingStore>();
  const getContactsDetails = usePatientStore(state => state.getContactsDetails);
  const getPatients = usePatientStore(state => state.getPatients);
  const practiceId = useOnlineBookingStore(state => state.practiceId);
  const setCurrentPatient = useOnlineBookingStore(
    state => state.setCurrentPatient,
  );
  const getFormConfig = useOnlineBookingStore(state => state.getFormConfig);
  const getGeneralInfo = useOnlineBookingStore(state => state.getGeneralInfo);
  const getShouldSave = useOnlineBookingStore(state => state.getShouldSave);
  const setShouldSave = useOnlineBookingStore(state => state.setShouldSave);
  const showTyp = useOnlineBookingStore(state => state.showTyp);
  const typShowCancelCTA = useOnlineBookingStore(
    state =>
      (state.generalConfig?.customization?.typShowCancel &&
        !isFromRescheduleFlow) ??
      false,
  );
  const typShowRescheduleCTA = useOnlineBookingStore(
    state =>
      (state.generalConfig?.customization?.typShowReschedule &&
        !isFromRescheduleFlow) ??
      false,
  );

  const integrationMode = useOnlineBookingStore(state => state.integrationMode);
  const rescheduleUrl = useOnlineBookingStore(state => state.rescheduleUrl);
  const cancelUrl = useOnlineBookingStore(state => state.cancelUrl);

  const [bodyToSend, setBodyToSend] = useState<AppointmentBody | undefined>(
    undefined,
  );
  const { getTextColor } = useGetTextColor();

  const [appointmentsInSuccess, setAppointmentsInSuccess] = useState<
    { appointmentId: string; label: string }[]
  >([]);

  //logica di salvataggio
  const {
    mutate: save,
    data: saveData,
    isSuccess: saveIsSuccess,
    isLoading: saveIsLoading,
    isError: saveIsError,
    // status,
    error,
  } = usePutAppointment(getGeneralInfo()?.organizationId);

  // #endregion Hooks

  // #region Functions

  const handleReschedule = useCallback(
    (appointmentId: string) => {
      const pathWithQueryParam = `${Urls.RESCHEDULE}?appointmentId=${appointmentId}`;
      if (integrationMode === IntegrationModes.STANDALONE) {
        //we're not using hash routing: we just need to redirect to reschedule URL with appointmentId.
        history.push(pathWithQueryParam);
      } else if (isEmbeddedIntegrationMode(integrationMode)) {
        // we're using hash routing: we need an href to reschedule URL from the host URL + # + reschedule path with appointmentId.
        const cleanedUrl = rescheduleUrl?.replace(/&apptId=[^&#]*/g, '');
        window.location.href = `${cleanedUrl}#${pathWithQueryParam}`;
      }
    },
    [rescheduleUrl, Urls.RESCHEDULE],
  );

  const handleCancel = useCallback(
    (appointmentId: string) => {
      const pathWithQueryParam = `${Urls.MANAGE}?mode=cancel&appointmentId=${appointmentId}`;
      if (integrationMode === IntegrationModes.STANDALONE) {
        //we're not using hash routing: we just need to redirect to cancel URL with query params.
        history.push(pathWithQueryParam);
      } else if (isEmbeddedIntegrationMode(integrationMode)) {
        // we're using hash routing: we need an href to cancel URL from the host URL + # + cancel path with query params.
        const cleanedUrl = cancelUrl?.replace(/&apptId=[^&#]*/g, '');
        window.location.href = `${cleanedUrl}#${pathWithQueryParam}`;
      }
    },
    [cancelUrl, Urls.MANAGE],
  );

  const addToCalendar = () => {
    // prendo direttamente i dati dallo stato perché gli store sono già stati cancellati
    const generalInfo = onlineBookingStoreCopy?.generalInfo;
    const patients = patientStoreCopy?.patients;

    const events: EventAttributes[] = [];
    patients?.forEach(p => {
      if (p?.appointmentId && p.scheduleExam) {
        const title = 'Visit at ' + generalInfo?.name;
        const description =
          'Booking for ' +
          p.patientDetails.firstName +
          ' ' +
          p.patientDetails.lastName +
          (p.scheduleExam.doctor ? ' with Dr. ' + p.scheduleExam.doctor : '');
        const location =
          generalInfo?.address.line[0] +
          (generalInfo?.address.city ? ', ' + generalInfo?.address.city : '') +
          (generalInfo?.address.postalCode
            ? ', ' + generalInfo?.address.postalCode
            : '');
        const organizer: EventAttributes['organizer'] = {
          name: p.scheduleExam.doctor,
        };

        // Convertire le date in un formato accettabile da ics (es: [2024, 10, 12, 9, 0])
        const startTime = new Date(p.scheduleExam.slotId.startTime);
        const endTime = new Date(p.scheduleExam.slotId.endTime);

        const start: EventAttributes['start'] = [
          startTime.getFullYear(),
          startTime.getMonth() + 1, // Mese da 0 a 11, per questo aggiungiamo 1
          startTime.getDate(),
          startTime.getHours(),
          startTime.getMinutes(),
        ];
        const end: EventAttributes['start'] = [
          endTime.getFullYear(),
          endTime.getMonth() + 1,
          endTime.getDate(),
          endTime.getHours(),
          endTime.getMinutes(),
        ];

        // Creare un oggetto evento
        events.push({
          start,
          end,
          title,
          organizer,
          description,
          location,
          status: 'CONFIRMED',
        });
      }
    });

    // Genera il file .ics per tutti gli eventi
    events.forEach((event, index) => {
      createEvent(event, (error, value) => {
        if (error) {
          console.error('Error during event creation:', error);
          return;
        }

        // Creare un Blob per il download del file
        const blob = new Blob([value], { type: 'text/calendar' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `event_${index + 1}.ics`; // Scarica ciascun evento con nome progressivo
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); // Pulizia dopo il download
      });
    });
  };

  // #endregion Functions

  // #region Effects

  useEffect(() => {
    // chiamati tutti col get per averli aggiornati
    const formConfigs = getFormConfig();
    const shouldSave = getShouldSave();
    const generalInfo = getGeneralInfo();
    const patients = getPatients();
    const contactsDetails = getContactsDetails();
    const currentIntegrationMode =
      useOnlineBookingStore.getState().integrationMode;
    if (shouldSave && !bodyToSend && generalInfo?.location && formConfigs) {
      const toSend = buildAppointmentBodyForSave({
        practiceId,
        currentLocationId: generalInfo.location,
        patients,
        contactsDetails,
        formConfigs,
        integrationMode: currentIntegrationMode,
        isFromRescheduleFlow,
      });
      setBodyToSend(toSend);
      setShouldSave(false);
      console.log('#set body -----------', toSend);
    }
  }, []);

  useEffect(() => {
    if (bodyToSend) {
      console.log('#chiamata da fare...: ', bodyToSend);
      save(bodyToSend);
    }
  }, [bodyToSend]);

  // hookCustom for save information
  useEffect(() => {
    // se la save va in success e ho i dati
    if (saveIsSuccess && !saveIsLoading && saveData) {
      //salvo sullo state
      // setAppResponse(saveData);
      if (saveData.every(r => !r.success)) {
        // se tutti vanno in errore genero un errore io da mandare
        // setResInSuccess(false);
        // mando un codice specifico di errore
        console.log('# tutti gli appt sono falliti');
        setErrorType(ErrorCodes.ALL);
        // becco il primo patient in failed e lo imposto come currentPatient
        setCurrentPatient(0);
      } else {
        let firstFailed = true;
        const successAppointments: {
          appointmentId: string;
          label: string;
        }[] = [];
        // mappo l'appId dentro lo store per i pazienti in success
        patientStore.patients.forEach((patient, index) => {
          if (patient) {
            // trovo l'appId in base agli slot che mi sono arrivati
            let appointmentId = undefined;
            let isSuccess = false;
            const patientSlotId = patient?.scheduleExam?.slotId?.slotId
              ? [patient?.scheduleExam?.slotId.slotId]
              : patient?.scheduleExam?.slotId?.internalSlots.map(
                  i => i.slotId,
                ) || [];

            for (const appt of saveData) {
              const findIt = matchPatientWithSlotIds(
                patientSlotId,
                appt.slotIds,
              );
              if (findIt) {
                appointmentId = appt.appointmentId;
                isSuccess = appt.success; // solo per sicurezza
                break;
              }
            }
            console.log('#appointmentId: ', appointmentId, isSuccess);
            // se c'è lo aggiungo al paziente
            if (appointmentId && isSuccess) {
              patientStore.setAppointmentId(appointmentId, index);
              successAppointments.push({
                appointmentId,
                label: `${patient?.patientDetails?.firstName} ${patient?.patientDetails?.lastName}`,
              });
            } else if (firstFailed && !appointmentId && !isSuccess) {
              // becco il primo patient in failed e lo imposto come currentPatient
              setCurrentPatient(index);
              firstFailed = false;
            }
          }
        });
        //setto le opzioni per le cta di reschedule e cancel
        setAppointmentsInSuccess(successAppointments);

        // se tutti i pazienti sono in success salvo nello state ResInSuccess
        if (saveData.every(r => r.success)) {
          console.log('# tutti gli appt sono ok');
          // setResInSuccess(true);
          // becco il primo patient in failed e lo imposto come currentPatient
          setCurrentPatient(0);
          setErrorType(ErrorCodes.OK);
        } else {
          console.log('# controllo situazione misc');
          setErrorType(ErrorCodes.MISC);
        }
        setPatientStoreCopy(patientStore.getStore());
        setOnlineBookingStoreCopy(onlineBookingStore);
      }
    } else if (saveIsError && error) {
      // controllo di aver ricevuto degli errori e di non avere i dati della response
      // setto dentro lo state l'errore
      // setSaveError(error);
      //TODO: capire dove fare la reset
      console.log('#error: ', error);
      if (error.status === ErrorCodes.BLACKLIST) {
        // resetto lo store e il local storage
        console.log('# errore misc');
        setErrorType(ErrorCodes.BLACKLIST);
      } else if (error.status === ErrorCodes.GENERIC) {
        console.log('# errore generic');
        setErrorType(ErrorCodes.GENERIC);
      } else {
        console.log('# errore');
        // resetto lo store e il local storage
        setErrorType(ErrorCodes.SERVICE);
      }
    }
  }, [error, saveData, saveIsError, saveIsLoading, saveIsSuccess]);

  useEffect(() => {
    console.log('##', onlineBookingStoreCopy, patientStoreCopy);
    if (
      onlineBookingStoreCopy &&
      patientStoreCopy &&
      ((saveIsSuccess && errorType === ErrorCodes.OK) || showTyp) //ensure that we arrived in success typ page (not in full error or misc error case)
    ) {
      //ANALYTICS (RONA only) - OEE/TAB Confirmation
      if (brandName) {
        const patients = patientStoreCopy.patients;

        const contactsDetailsEmail = patientStoreCopy.contactsDetails.email;
        const isemailOptinChecked =
          patientStoreCopy.contactsDetails.customFieldsMarketing.find(
            consent => {
              return consent.id == 'EMAIL';
            },
          )?.answer === 'true';

        trackPageAnalytics({
          page: FlowsPages.TYP,
          patients: patients,
          contactsDetailsEmail: contactsDetailsEmail,
          isEmailOptinChecked: isemailOptinChecked,
        });
      }
      //So che sono in success, ma dal momento che sto per svuotare lo store (resetApp()) perderò info utili come showTyp,
      // dunque setto l'error code a ok per evitare l'AppointmentErrorPage
      setErrorType(ErrorCodes.OK);
      resetApp(!!brandName);
      //if a callback is defined, trigger it
      if (window?.luxOB?.configurations?.onShowTYP) {
        window.luxOB.configurations.onShowTYP();
      }
    }
  }, [
    onlineBookingStoreCopy,
    patientStoreCopy,
    saveIsSuccess,
    errorType,
    showTyp,
  ]);

  // #endregion Effects

  // #region Memos

  const primaryButton = useMemo(() => {
    const c = config.customization;
    if (!c) return undefined;

    if (c.primaryButtonEnableAddToCalendar) {
      return {
        label: t('generalCta.addToCalendar'),
        link: '',
        hasIcon: true,
      };
    }

    if (c.primaryButtonLabel && c.primaryButtonLink) {
      return {
        label: c.primaryButtonLabel,
        link: c.primaryButtonLink,
      };
    }

    return undefined;
  }, [config.customization, t]);

  const secondaryButton = useMemo(() => {
    const c = config.customization;

    if (c?.secondaryButtonLabel && c.secondaryButtonLink) {
      return {
        label: c.secondaryButtonLabel,
        link: c.secondaryButtonLink,
      };
    } else {
      return undefined;
    }
  }, [config.customization]);

  const showButtonWrapper = useMemo(() => {
    return (
      typShowCancelCTA ||
      typShowRescheduleCTA ||
      primaryButton ||
      secondaryButton
    );
  }, [primaryButton, secondaryButton, typShowCancelCTA, typShowRescheduleCTA]);

  // #endregion Memos

  // #region JSX

  console.log('#typ situa', saveIsSuccess, errorType, showTyp);

  const RescheduleCTA: React.FC = () => {
    if (appointmentsInSuccess.length === 1) {
      return (
        <LuxButton
          variant="secondary"
          onClick={() =>
            handleReschedule(appointmentsInSuccess[0].appointmentId)
          }
          iconStart="EditIcon"
          style={{
            whiteSpace: 'nowrap',
            width: isMobile ? '100%' : 'unset',
          }}
        >
          <LuxTypography
            variant={'textMdRegular'}
            fontWeight={brandName && '700!important'}
          >
            {conditionallyApplyUpperCase(
              t('thankYouMessages.rescheduleAppt'),
              brandName,
            )}
          </LuxTypography>
        </LuxButton>
      );
    }
    if (appointmentsInSuccess.length > 1) {
      return (
        <LuxSplitedButton
          buttonVariant="secondary"
          splittedOptions={appointmentsInSuccess.map(opt => ({
            label: opt.label,
            callback: () => handleReschedule(opt.appointmentId),
          }))}
          label={String(t('thankYouMessages.rescheduleAppt'))}
          style={{
            whiteSpace: 'nowrap',
            textTransform:
              brandName && brandName !== BrandNameList.FE
                ? 'uppercase'
                : 'none',
          }}
          splitButtonSx={{ width: isMobile ? '100%' : 'unset' }}
        />
      );
    }
    return null;
  };

  const CancelCTA: React.FC = () => {
    if (appointmentsInSuccess.length === 1) {
      return (
        <LuxButton
          variant="secondary"
          onClick={() => handleCancel(appointmentsInSuccess[0].appointmentId)}
          iconStart="DeleteIcon"
          style={{
            whiteSpace: 'nowrap',
            width: isMobile ? '100%' : 'unset',
          }}
        >
          <LuxTypography
            variant={'textMdRegular'}
            fontWeight={brandName && '700!important'}
          >
            {conditionallyApplyUpperCase(
              t('thankYouMessages.cancelAppt'),
              brandName,
            )}
          </LuxTypography>
        </LuxButton>
      );
    }
    if (appointmentsInSuccess.length > 1) {
      return (
        <LuxSplitedButton
          buttonVariant="secondary"
          splittedOptions={appointmentsInSuccess.map(opt => ({
            label: opt.label,
            callback: () => handleCancel(opt.appointmentId),
          }))}
          label={String(t('thankYouMessages.cancelAppt'))}
          style={{
            whiteSpace: 'nowrap',
            textTransform:
              brandName && brandName !== BrandNameList.FE
                ? 'uppercase'
                : 'none',
          }}
          splitButtonSx={{ width: isMobile ? '100%' : 'unset' }}
        />
      );
    }
    return null;
  };

  return (
    <>
      <GlobalStyles
        styles={{
          body: {
            '@media print': {
              // transform: 'scale(0.8)',
              '.ob-no-print': {
                display: 'none',
              },
              '@page': {
                size: 'A4',
                margin: 'auto',
              },
            },
          },
        }}
      />
      {saveIsLoading ? (
        <ExternalWrapper
          backgroundColor={
            brandName && theme.palette.LUX.obTokens.bgColorBaseSecondary
          }
        >
          <LuxLoader height="4rem" />
        </ExternalWrapper>
      ) : (
        <>
          {/* <LuxButton onClick={() => setShowTyp(false)}>reset showTyp</LuxButton> */}
          {/* Se sono in success o se nel caso misc voglio procedere lo stesso mostro la typ */}
          {((saveIsSuccess && errorType === ErrorCodes.OK) || showTyp) && (
            <>
              <ExternalWrapper
                backgroundColor={
                  brandName && theme.palette.LUX.obTokens.bgColorBaseSecondary
                }
              >
                <TypWrapper>
                  <ThankYouMessageWrapper className="ob-no-print">
                    <Stack
                      style={{
                        marginTop: '1.5rem',
                        width: '2.75rem',
                        height: '2.75rem',
                        borderRadius: '100%',
                        border: `1px solid ${theme.palette.LUX.obTokens.bordersColorFunctionalSuccess}`,
                        backgroundColor:
                          theme.palette.LUX.obTokens.bgColorFunctionalSuccess,
                      }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <CheckIcon
                        color={theme.palette.LUX.content.primary}
                        size="small"
                      />
                    </Stack>
                    <SubtitleWrapper>
                      <LuxTypography variant="heading3">
                        {t('thankYouMessages.appointmentBooked')}
                      </LuxTypography>
                      <LuxTypography variant="textMdRegular">
                        {t('thankYouMessages.cantWaitToSeeYou')}
                      </LuxTypography>
                    </SubtitleWrapper>
                    {config.customization?.appointmentConfirmationMessage && (
                      <MessageWrapper>
                        <LuxTypography align="center" variant="textMdRegular">
                          {t(
                            config.customization.appointmentConfirmationMessage,
                          )}
                        </LuxTypography>
                      </MessageWrapper>
                    )}
                  </ThankYouMessageWrapper>
                  {patientStoreCopy && onlineBookingStoreCopy && (
                    <BookingDetailsTyp
                      patientStore={patientStoreCopy}
                      onlineBookingStore={onlineBookingStoreCopy}
                      isFullRecap
                      hideEditButton
                      checkAppId
                    />
                  )}
                </TypWrapper>
              </ExternalWrapper>
              {showButtonWrapper && (
                <ButtonsWrapper
                  fromTyp
                  onlySecondary={
                    primaryButton &&
                    !secondaryButton &&
                    !typShowCancelCTA &&
                    !typShowRescheduleCTA
                  }
                  step={
                    primaryButton &&
                    !secondaryButton &&
                    !typShowCancelCTA &&
                    !typShowRescheduleCTA
                      ? 0
                      : -1
                  }
                  className="ob-no-print"
                >
                  <LeftButtonsWrapper>
                    {secondaryButton && (
                      <LuxButton
                        variant="secondary"
                        // iconStart="HomeIcon"
                        onClick={() =>
                          window.open(secondaryButton.link, '_blank')
                        }
                      >
                        <LuxTypography
                          variant={'textMdRegular'}
                          fontWeight={brandName && '700!important'}
                        >
                          {conditionallyApplyUpperCase(
                            secondaryButton.label || '',
                            brandName,
                          )}
                        </LuxTypography>
                      </LuxButton>
                    )}
                    {typShowRescheduleCTA && <RescheduleCTA />}
                    {typShowCancelCTA && <CancelCTA />}
                  </LeftButtonsWrapper>
                  {primaryButton && (
                    <LuxButton
                      iconStart={
                        config.customization?.primaryButtonEnableAddToCalendar
                          ? 'CalendarIcon'
                          : undefined
                      }
                      textColor={getTextColor(
                        theme.palette.LUX.obTokens?.actionColorBgBrand,
                      )}
                      customColor={
                        theme.palette.LUX.obTokens?.actionColorBgBrand
                      }
                      onClick={() =>
                        config.customization?.primaryButtonEnableAddToCalendar
                          ? addToCalendar()
                          : window.open(primaryButton.link, '_blank')
                      }
                      style={{
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <LuxTypography
                        variant={'textMdRegular'}
                        fontWeight={brandName && '700!important'}
                      >
                        {conditionallyApplyUpperCase(
                          primaryButton.label || '',
                          brandName,
                        )}
                      </LuxTypography>
                    </LuxButton>
                  )}
                </ButtonsWrapper>
              )}
            </>
          )}
          {/* Altrimenti se vado in errore passo la pagina di errore con l'errore i dati  */}
          {errorType && errorType !== ErrorCodes.OK && !showTyp && (
            <AppointmentErrorPage
              res={saveData}
              errorType={errorType}
              error={error || undefined}
            />
          )}
        </>
      )}
    </>
  );

  // #endregion JSX
};

export default ThankYouPage;
